/*
 * BULK-APPROVE-DIALOG - Dialog for bulk approving/rejecting timesheet records
 * 
 * SUMMARY:
 * Enhanced to support periodic approval (daily, weekly, monthly) and capturing comments.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { bulkUpdateTimesheet } from '../../actions/timesheet.action';
import { warningSnackBar } from '../../actions/common.action';
import TimesheetActionConfirmation from '../../components/timesheet-action-confirmation-dialog';
import ReviewCommentModal from '../../components/review-comment-modal';
import moment from 'moment';
import _ from 'lodash';
import './scss/timesheet.scss';
import CustomTable from '../../components/table/table';
import Checkbox from '@mui/material/Checkbox';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { ChatBubbleOutline } from '@mui/icons-material';

const BulkApproveDialog = ({ open, onClose, timesheets = [], onSuccess, title }) => {
  const [groupedTimesheets, setGroupedTimesheets] = useState({});
  const [selectedTimesheets, setSelectedTimesheets] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectAll, setSelectAll] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [reviewCommentModalOpen, setReviewCommentModalOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState("");

  const dispatch = useDispatch()

  useEffect(() => {
    if (open && timesheets.length > 0) {
      // Group timesheets by user
      const grouped = _.groupBy(timesheets, 'userId');
      setGroupedTimesheets(grouped);

      // Initialize selection state
      const initialSelected = {};
      Object.keys(grouped).forEach(userId => {
        initialSelected[userId] = {};
        grouped[userId].forEach(timesheet => {
          initialSelected[userId][timesheet.id] = false;
        });
      });
      setSelectedTimesheets(initialSelected);

      // Initialize selectAll state
      const initialSelectAll = {};
      Object.keys(grouped).forEach(userId => {
        initialSelectAll[userId] = false;
      });
      setSelectAll(initialSelectAll);
    }
  }, [open, timesheets]);

  const handleSelectTimesheet = (userId, timesheetId) => {
    setSelectedTimesheets(prev => {
      const newSelected = {
        ...prev,
        [userId]: {
          ...prev[userId],
          [timesheetId]: !prev[userId][timesheetId]
        }
      };

      // Update selected count
      updateSelectedCount(newSelected);
      return newSelected;
    });

    // Update selectAll state based on selections
    const allSelected = Object.values(selectedTimesheets[userId] || {}).every(val => val);
    setSelectAll(prev => ({
      ...prev,
      [userId]: !allSelected
    }));
  };

  const updateSelectedCount = (selectedTimesheets) => {
    let count = 0;
    Object.keys(selectedTimesheets).forEach(userId => {
      Object.keys(selectedTimesheets[userId]).forEach(timesheetId => {
        if (selectedTimesheets[userId][timesheetId]) {
          count++;
        }
      });
    });
    setSelectedCount(count);
  };

  /**
   * Flattens currently selected rows for validation before action requests.
   *
   * @returns {Array<Object>} Selected timesheet rows.
   */
  const getSelectedRows = () => {
    const selectedRows = [];
    Object.keys(selectedTimesheets).forEach(userId => {
      Object.keys(selectedTimesheets[userId]).forEach(timesheetId => {
        if (selectedTimesheets[userId][timesheetId]) {
          const userRecords = groupedTimesheets[userId] || [];
          const record = userRecords.find(r => r.id === timesheetId);
          if (record) selectedRows.push(record);
        }
      });
    });
    return selectedRows;
  };

  const handleSelectAllForUser = (userId) => {
    const newSelectAll = !selectAll[userId];
    setSelectAll(prev => ({
      ...prev,
      [userId]: newSelectAll
    }));

    // Select or deselect all timesheets for this user
    const userTimesheets = { ...selectedTimesheets[userId] };
    Object.keys(userTimesheets).forEach(timesheetId => {
      userTimesheets[timesheetId] = newSelectAll;
    });

    setSelectedTimesheets(prev => {
      const newSelected = {
        ...prev,
        [userId]: userTimesheets
      };

      // Update selected count
      updateSelectedCount(newSelected);
      return newSelected;
    });
  };

  const requestApproval = (status) => {
    const selectedRows = getSelectedRows();
    const selectedIds = selectedRows.map(row => row.id);

    if (selectedIds.length === 0) {
      dispatch(warningSnackBar('Please select at least one timesheet to process'));
      return;
    }

    if (status === 'APPROVED') {
      const manualReviewRows = selectedRows.filter(row => row.requiresManualReview);
      if (manualReviewRows.length > 0) {
        dispatch(
          warningSnackBar(
            `${manualReviewRows.length} selected record(s) require manual review and cannot be bulk approved.`
          )
        );
        return;
      }
    }

    setPendingAction(status);
    setReviewCommentModalOpen(true);
  };

  const handleReviewCommentSubmit = (comment) => {
    setReviewComment(comment);
    setReviewCommentModalOpen(false);
    setShowConfirmation(true);
  };

  const confirmApproval = async (status, comment) => {
    setShowConfirmation(false);

    // Collect all selected timesheet IDs and build userNameMap
    const selectedIds = [];
    const userNameMap = {};
    Object.keys(selectedTimesheets).forEach(userId => {
      Object.keys(selectedTimesheets[userId]).forEach(timesheetId => {
        if (selectedTimesheets[userId][timesheetId]) {
          selectedIds.push(timesheetId);
          // Find the timesheet record to get the employee name
          const userRecords = groupedTimesheets[userId] || [];
          const record = userRecords.find(r => r.id === timesheetId);
          if (record) {
            userNameMap[timesheetId] = record.empName;
          }
        }
      });
    });

    setIsProcessing(true);

    try {
      await dispatch(bulkUpdateTimesheet({
        allocationIds: selectedIds,
        status: status,
        comment: comment || reviewComment || "",
        userNameMap
      }));

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error processing timesheets:', error);
    } finally {
      setIsProcessing(false);
      setPendingAction(null);
      setReviewComment("");
    }
  };

  // const handleApproveSelected = async (status) => {
  //   await confirmApproval(status);
  // };

  const getUserName = (userId) => {
    const userTimesheets = groupedTimesheets[userId] || [];
    if (userTimesheets.length > 0) {
      return userTimesheets[0].empName || `User ID: ${userId}`;
    }
    return `User ID: ${userId}`;
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      enforceFocus={!reviewCommentModalOpen}
      size="lg"
      className="bulk-approve-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className='brand-color-1'>{title || 'Review Timesheet Records'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {Object.keys(groupedTimesheets).length === 0 ? (
          <div className="text-center p-4">
            <p>No timesheet records in review status found.</p>
          </div>
        ) : (
          <div className="bulk-approve-container">
            {Object.keys(groupedTimesheets).map(userId => (
              <div key={userId} className="user-timesheet-group mb-4">
                <div className="user-header d-flex align-items-center p-2" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <div className="align-items-center d-flex form-check">
                    <Checkbox className='p-1 w-2 me-3' size='small' checked={selectAll[userId]} onChange={() => handleSelectAllForUser(userId)} id={`select-all-${userId}`} />
                    {/* <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectAll[userId]}
                      onChange={() => handleSelectAllForUser(userId)}
                      id={`select-all-${userId}`}
                    /> */}
                    <label className="form-check-label fw-bold brand-color-1" htmlFor={`select-all-${userId}`}>
                      {getUserName(userId)}
                    </label>
                  </div>
                </div>

                <div className="timesheet-list pt-3">
                  <CustomTable
                    columns={[
                      {
                        field: 'select',
                        title: '',
                        render: (item) => (
                          <div className="d-flex align-items-center gap-1">
                            <Checkbox size='small' checked={selectedTimesheets[userId]?.[item.id] || false} onChange={() => handleSelectTimesheet(userId, item.id)} />
                            {item.comment && (
                              <OverlayTrigger
                                trigger="click"
                                rootClose
                                placement="auto"
                                overlay={
                                  <Popover id={`popover-comment-bulk-${item.id}`} style={{ maxWidth: '400px' }}>
                                    <Popover.Header as="h3">Review Comment</Popover.Header>
                                    <Popover.Body style={{
                                      maxHeight: '300px',
                                      overflowY: 'auto',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      fontSize: '13px',
                                      lineHeight: '1.4'
                                    }}>
                                      {item.comment}
                                    </Popover.Body>
                                  </Popover>
                                }
                              >
                                <ChatBubbleOutline
                                  fontSize="small"
                                  style={{ cursor: "pointer", color: "#1976d2", fontSize: '1rem' }}
                                />
                              </OverlayTrigger>
                            )}
                          </div>
                        ),
                        class: 'w-2'
                      },
                      {
                        field: 'projectName',
                        title: 'Project',
                        class: 'w-30'
                      },
                      {
                        field: 'taskHierachy',
                        title: "Section\\Task",
                        class: 'w-30'
                      },
                      {
                        field: 'startTime',
                        title: 'Start & End time',
                        class: 'text-nowrap',
                        render: (item) => `${moment(item.startTime).format('DD-MM-YYYY hh:mm a')} - ${moment(item.endTime).format('hh:mm a')}`
                      },
                      //   { 
                      //     field: 'hours', 
                      //     title: 'Normal Hours',
                      //     class: 'text-center text-nowrap',
                      //     render: (item) => `${(moment.duration(
                      //       moment(item.endTime).diff(moment(item.startTime))
                      //     ).asHours() - (item.overtime1 + item.overtime2)).toFixed(2)}`
                      //   },
                      {
                        field: 'overtime1',
                        title: 'OT1',
                        class: 'text-center text-nowrap',
                        render: (item) => `${item.overtime1}`
                      },
                      {
                        field: 'overtime2',
                        title: 'OT2',
                        class: 'text-center text-nowrap',
                        render: (item) => `${item.overtime2}`
                      }
                    ]}
                    data={groupedTimesheets[userId]}
                    enableSearch={true}
                    exportButton={true}
                    toolbar={true}
                    itemsPerPageCustom={false}
                    limit={100}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between'>
        <Button className='btn-bg-brand-5 border-0' onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
        <div className='d-flex gap-2'>
          <Button
            className='btn-bg-brand-5 border-0'
            onClick={() => requestApproval('REJECTED')}
            disabled={isProcessing || Object.keys(groupedTimesheets).length === 0}
          >
            {isProcessing ? 'Processing...' : 'Reject Selected'}
          </Button>
          <Button
            className='btn-bg-brand-3 border-0'
            onClick={() => requestApproval('APPROVED')}
            disabled={isProcessing || Object.keys(groupedTimesheets).length === 0}
          >
            {isProcessing ? 'Processing...' : 'Approve Selected'}
          </Button>
        </div>
      </Modal.Footer>

      {/* Review Comment Modal */}
      <ReviewCommentModal
        open={reviewCommentModalOpen}
        onClose={() => {
          setReviewCommentModalOpen(false);
          setPendingAction(null);
          setReviewComment("");
        }}
        onSubmit={handleReviewCommentSubmit}
        action={pendingAction}
        isBulk={true}
        count={selectedCount}
      />

      {/* Confirmation Dialog */}
      <TimesheetActionConfirmation
        open={showConfirmation}
        dialogClose={() => setShowConfirmation(false)}
        onConfirm={(action, confirmationComment) => {
          confirmApproval(action, reviewComment || confirmationComment);
        }}
        action={pendingAction}
        count={selectedCount}
        isBulk={true}
        hideCommentField={true}
      />
    </Modal>
  );
};

export default BulkApproveDialog; 